import React, { useState } from 'react';
import apiService from '../../services/api';
import { useNotifications } from '../../hooks/useNotifications';

/**
 * Recruiting an affiliate.
 *
 * Moved from pages/AdminDashboard.tsx, which was the only place this existed —
 * the console could issue a discount code to an affiliate but could not create
 * one, so recruiting meant leaving the console for the older dashboard.
 *
 * Rebuilt against the semantic tokens rather than carried across: the original
 * used gray-300/gray-500 for labels and help text, which sit at 2.8:1 and 4.0:1
 * on white and fail the 4.5:1 floor the rest of the console now meets.
 *
 * The brand voice in the copy is kept. "Chill Setter" and "Treasure Chest" are
 * what these are called everywhere else the team looks at them, and renaming
 * them here would only make this screen the odd one out.
 */

interface CreateAffiliateProps {
  /** Lets the affiliates list refresh itself once a new one exists. */
  onCreated?: () => void;
}

const EMPTY = { name: '', email: '', walletInput: '', createDiscount: true, discountCode: '' };

const CreateAffiliate: React.FC<CreateAffiliateProps> = ({ onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [isSaving, setIsSaving] = useState(false);
  const { addNotification } = useNotifications();

  const set = <K extends keyof typeof EMPTY>(key: K, value: (typeof EMPTY)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const result = await apiService.createAffiliate({
        name: form.name,
        email: form.email,
        walletInput: form.walletInput,
        createDiscount: form.createDiscount,
        // Omitted when blank: the endpoint treats a present value as a
        // deliberate choice and rejects an empty one rather than deriving.
        ...(form.createDiscount && form.discountCode.trim()
          ? { discountCode: form.discountCode.trim() }
          : {}),
      });

      const discount = (result.data as { discount?: { code: string; amountOff: number } })?.discount;

      addNotification({
        type: 'success',
        title: 'Chill Setter recruited',
        message: discount
          ? `Their code is ${discount.code} for $${(discount.amountOff / 100).toFixed(2)} off.`
          : 'Chill Setter created successfully.',
      });

      // The affiliate exists but the code does not. Its own warning rather than
      // folded into the success message, because someone who believes a code
      // exists will publish it.
      if (result.warning) {
        addNotification({
          type: 'error',
          title: 'No discount code',
          message: `${result.warning} You can attach one from the affiliates list.`,
        });
      }

      setForm(EMPTY);
      onCreated?.();
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Could not recruit',
        message: error instanceof Error ? error.message : 'Failed to create the Chill Setter.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const field = 'w-full px-4 py-3 bg-surface border border-border-default rounded-xl text-ink placeholder-ink-3 focus:outline-none focus:border-info transition-colors';

  return (
    <div className="bg-surface rounded-2xl p-6 border border-border-subtle shadow-sm max-w-2xl">
      <h3 className="text-xl font-title font-bold text-ink mb-1">Recruit a Chill Setter</h3>
      <p className="text-ink-2 mb-6">Add a cultural curator who can spark discovery.</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="affiliateName" className="block text-ink font-bold mb-2">
            Curator name <span className="text-negative" aria-hidden="true">*</span>
          </label>
          <input
            type="text"
            id="affiliateName"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            required
            className={field}
            placeholder="Enter curator name"
          />
        </div>

        <div>
          <label htmlFor="affiliateEmail" className="block text-ink font-bold mb-2">
            Contact channel <span className="text-negative" aria-hidden="true">*</span>
          </label>
          <input
            type="email"
            id="affiliateEmail"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            required
            className={field}
            placeholder="Enter contact email"
          />
        </div>

        <div>
          <label htmlFor="affiliateWallet" className="block text-ink font-bold mb-2">
            Treasure chest <span className="text-negative" aria-hidden="true">*</span>
          </label>
          <input
            type="text"
            id="affiliateWallet"
            value={form.walletInput}
            onChange={(e) => set('walletInput', e.target.value)}
            required
            className={`${field} font-mono text-sm`}
            placeholder="0x… or yourname.eth"
            aria-describedby="affiliateWalletHelp"
          />
          <p id="affiliateWalletHelp" className="text-ink-3 text-sm mt-2">
            Their wallet address, for when cool things happen. An ENS name is resolved for you.
          </p>
        </div>

        {/* The $5-off code that ships with their link: applied automatically to
            anyone arriving on it, and typeable at checkout by anyone who just
            hears them say it. */}
        <div className="border-t border-border-subtle pt-6">
          <label htmlFor="createDiscount" className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              id="createDiscount"
              checked={form.createDiscount}
              onChange={(e) => set('createDiscount', e.target.checked)}
              className="mt-1 h-4 w-4 accent-info"
            />
            <span className="text-ink font-bold">
              Give them a $5 discount code
              <span className="block text-ink-2 text-sm font-normal">
                Applied automatically when someone arrives on their link, and redeemable at
                checkout by anyone who types it.
              </span>
            </span>
          </label>

          {form.createDiscount && (
            <div className="mt-4">
              <label htmlFor="discountCode" className="block text-ink font-bold mb-2">Code</label>
              <input
                type="text"
                id="discountCode"
                value={form.discountCode}
                onChange={(e) => set('discountCode', e.target.value.toUpperCase())}
                className={`${field} font-mono`}
                placeholder="Leave blank to use their name, e.g. ADA5"
                maxLength={20}
                aria-describedby="discountCodeHelp"
              />
              <p id="discountCodeHelp" className="text-ink-3 text-sm mt-2">
                Letters and digits only — no spaces or dashes. If the code is taken, a letter is
                added to the end.
              </p>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isSaving}
          className="w-full px-4 py-3 rounded-xl bg-ink text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Recruiting…' : 'Recruit Chill Setter'}
        </button>
      </form>
    </div>
  );
};

export default CreateAffiliate;
